"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.SnackBar = void 0;
require("./SnackBar.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _renderObject = require("../../util/renderObject/renderObject");
var _snackbarUtil = require("../../util/snackbarUtil/snackbarUtil");
var _Icon = _interopRequireDefault(require("../Icon/Icon"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * SnackBar component
 *
 * @method
 * @param { SnackBarProps } props
 * @returns SnackBar
 */

const SnackBar = props => {
  const {
    animationClassName,
    snackBarId,
    title,
    iconName,
    borderColor = defaultProps.borderColor,
    backgroundColor = defaultProps.backgroundColor,
    textColor = defaultProps.textColor,
    iconColor = defaultProps.iconColor,
    theme = defaultProps.theme,
    externalActionElement,
    externalMediaElement,
    style = defaultProps.style,
    className
  } = props;
  const resolvedStyles = (0, _snackbarUtil.getSnackBarDefaultStyles)({
    style,
    borderColor,
    backgroundColor,
    textColor,
    iconColor
  });
  const showThumbnail = !!externalMediaElement && (style === 'thumbnail' || style === 'unstyled');
  let mediaElement = null;
  if (showThumbnail) {
    mediaElement = /*#__PURE__*/_react.default.createElement("div", {
      className: "pal-c-SnackBar__thumbnail"
    }, (0, _renderObject.renderObject)(externalMediaElement));
  } else if (iconName) {
    mediaElement = /*#__PURE__*/_react.default.createElement(_Icon.default, {
      name: iconName,
      size: "lg",
      className: "pal-c-SnackBar__icon"
    });
  }
  const componentClassName = (0, _classnames.default)('pal-c-SnackBar', {
    'pal-c-SnackBar--backgroundColor': !!resolvedStyles.backgroundColor,
    'pal-c-SnackBar--borderColor': !!resolvedStyles.borderColor,
    [`pal-c-SnackBar--${animationClassName}`]: animationClassName
  }, className);
  const containerStyle = {
    '--borderColor': `var( --pal-colors-${theme}-${resolvedStyles.borderColor} )`,
    '--backgroundColor': `var( --pal-colors-${theme}-${resolvedStyles.backgroundColor} )`,
    '--textColor': `var( --pal-colors-${theme}-content-${resolvedStyles.textColor} )`,
    '--iconColor': `var( --pal-colors-${theme}-content-${resolvedStyles.iconColor} )`
  };
  return /*#__PURE__*/_react.default.createElement("div", {
    className: componentClassName,
    style: containerStyle,
    key: snackBarId
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-SnackBar__content",
    role: "alert"
  }, mediaElement, title && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-SnackBar__titleContainer"
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "pal-c-SnackBar__title"
  }, title)), externalActionElement && (0, _renderObject.renderObject)(externalActionElement)));
};

/**
 * Property type definitions
 * @typedef SnackBarProps
 * @type {object}
 * @property {string} title - Title text of the SnackBar.
 * @property {string} iconName - Name of the icon which renders on the left side of the SnackBar.
 * @property {string} borderColor - Sets the border color of the SnackBar container.
 * @property {string} backgroundColor - Sets the background color of the SnackBar container.
 * @property {string} textColor - Sets the text color of the title.
 * @property {string} iconColor - Sets the color of the icon.
 * @property {string} theme - Sets the theme of the SnackBar.
 * @property {object} externalActionElement - Accepts action element from web-site with additional logic.
 * @property {object} externalMediaElement - Accepts thumbnail image from web-site.
 * @property {string} style - Sets the pre styled variants (default, thumbnail, error, unstyled).
 * @property {string} snackBarId - Sets the key of SnackBar container.
 * @property {string} animationClassName - Sets the animation for the snackbar (SlideIn, FadeOut).
 */
exports.SnackBar = SnackBar;
const propTypes = exports.propTypes = {
  /**
   * Title text of the SnackBar
   */
  title: _propTypes.default.string,
  /**
   * Name of the icon which renders on the left side of the SnackBar
   */
  iconName: _propTypes.default.string,
  /**
   * Sets the border color of the SnackBar container
   */
  borderColor: _propTypes.default.string,
  /**
   * Sets the background color of the SnackBar container
   */
  backgroundColor: _propTypes.default.string,
  /**
   * Sets the text color of the title
   */
  textColor: _propTypes.default.string,
  /**
   * Sets the color of the icon
   */
  iconColor: _propTypes.default.string,
  /**
   * Sets the theme of the SnackBar
   */
  theme: _propTypes.default.string,
  /**
   * Accepts action element from web-site with additional logic
   */
  externalActionElement: _propTypes.default.object,
  /**
   * Accepts thumbnail image from web-site
   */
  externalMediaElement: _propTypes.default.object,
  /**
   * Sets the pre styled variants (default, thumbnail, error, unstyled)
   */
  style: _propTypes.default.oneOf(['default', 'thumbnail', 'error', 'unstyled']),
  /**
   * Sets the key of SnackBar container
   */
  snackBarId: _propTypes.default.string,
  /**
   * Sets the animation for the snackbar (SlideIn, FadeOut)
   */
  animationClassName: _propTypes.default.string
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} borderColor - border-notification - Sets the default border color to border-notification.
 * @property {string} backgroundColor - background-notification - Sets the default background color to background-notification.
 * @property {string} textColor - default - Sets the default text color to default.
 * @property {string} iconColor - default - Sets the default icon color to default.
 * @property {string} theme - default - Sets the default theme to default.
 * @property {string} style - default - Sets the default style to default.
 */
const defaultProps = exports.defaultProps = {
  borderColor: 'border-notification',
  backgroundColor: 'background-notification',
  textColor: 'default',
  iconColor: 'default',
  theme: 'default',
  style: 'default'
};
SnackBar.propTypes = propTypes;
SnackBar.defaultProps = defaultProps;
var _default = exports.default = SnackBar;